"use client";
import { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import {
  Tooltip,
  Button,
  message as staticMessage,
  App,
  Modal,
  Flex,
  Typography,
  Divider,
} from "antd";
import {
  IconShare,
  IconCopy,
  IconCalendarEvent,
  IconMapPin,
} from "@/lib/icons";
import styles from "./Event/event.module.css";
import { storeReferralCode } from "@/lib/referralCode";
import { trackShare } from "@/lib/analytics";
import { trackCustomEvent } from "@/lib/metaPixel";
import {
  FacebookShareButton,
  FacebookIcon,
  TwitterShareButton,
  TwitterIcon,
  WhatsappShareButton,
  WhatsappIcon,
  LinkedinShareButton,
  LinkedinIcon,
  EmailShareButton,
  EmailIcon,
  RedditShareButton,
  RedditIcon,
} from "react-share";
import { getConvertedEventDateTime } from "@/lib/formatDateTime";
import { buildShareText } from "@/lib/shareMessage";
import Image from "next/image";

const { Text, Title } = Typography;

const ShareButton = ({
  external_link,
  link,
  referral_code,
  eventId,
  eventDetails,
  shareType = "event",
  showLabel = false,
  label = "Share",
  compact = false,
  className = "",
  fullName,
  children,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");

  const pathname = usePathname();

  const antdApp = App?.useApp ? App.useApp() : null;
  const messageApi = antdApp?.message || staticMessage;

  useEffect(() => {
    storeReferralCode(referral_code?.referral_code);

    let relativeOrAbsoluteLink = link || pathname || "";
    if (
      relativeOrAbsoluteLink &&
      !relativeOrAbsoluteLink.startsWith("/") &&
      !relativeOrAbsoluteLink.startsWith("http")
    ) {
      relativeOrAbsoluteLink = `/${relativeOrAbsoluteLink}`;
    }

    const baseUrl = (
      process.env.NEXT_PUBLIC_CUSTOMER_URL ||
      (typeof window !== "undefined" ? window.location.origin : "")
    ).replace(/\/$/, "");

    const fullCopyUrl = external_link
      ? external_link
      : relativeOrAbsoluteLink.startsWith("http")
        ? relativeOrAbsoluteLink
        : `${baseUrl}${relativeOrAbsoluteLink}`;

    setShareUrl(fullCopyUrl);
  }, [link, external_link, referral_code, pathname]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      trackShare({
        method: "link_copy",
        content_type: shareType || "event",
        item_id: eventId || link,
        event_id: eventId,
      });
      trackCustomEvent("Share", {
        method: "link_copy",
        content_type: shareType || "event",
        ...(eventId ? { event_id: eventId } : {}),
      });
      messageApi?.success?.("Link copied to clipboard!");
    } catch (err) {
      messageApi?.error?.("Failed to copy link.");
    }
  };

  const formattedDate =
    eventDetails?.start_date
      ? getConvertedEventDateTime(
          eventDetails.start_date,
          eventDetails.start_time,
          eventDetails.timezone,
          "ddd DD MMM YYYY, hh:mm A",
        )
      : eventDetails?.date || "";

  const displayTitle = eventDetails?.name || fullName || "";
  const venueLocation =
    eventDetails?.subtitle ||
    eventDetails?.venue?.name ||
    eventDetails?.venue?.city ||
    eventDetails?.city ||
    "";

  const shareText = buildShareText({
    type: shareType,
    title: displayTitle,
    details: [
      formattedDate && `📅 ${formattedDate}`,
      venueLocation && `📍 ${venueLocation}`,
    ],
  });

  return (
    <>
      {children ? (
        <div
          onClick={() => setIsModalOpen(true)}
          style={{ display: "inline-block", cursor: "pointer" }}
        >
          {children}
        </div>
      ) : compact ? (
        <button
          type="button"
          onClick={() => setIsModalOpen(true)}
          aria-label={`Share ${shareType}`}
          title={`Share ${shareType}`}
          className={`flex size-9 shrink-0 items-center justify-center rounded-full bg-[#F1F3F5] text-[#021526]/70 transition-colors hover:bg-[#E3E6E9] hover:text-[#021526] ${className}`}
        >
          <IconShare size={16} />
        </button>
      ) : showLabel ? (
        <button
          type="button"
          onClick={() => setIsModalOpen(true)}
          className={className}
          style={
            !className
              ? {
                  padding: "8px 16px",
                  borderRadius: "8px",
                  width: "auto",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  background: "#ffffff",
                  border: "1px solid #e2e8f0",
                  fontSize: "13.5px",
                  fontWeight: 500,
                  color: "#1e293b",
                  cursor: "pointer",
                }
              : undefined
          }
        >
          <IconShare size={16} /> {label}
        </button>
      ) : compact ? (
        <Button
          onClick={() => setIsModalOpen(true)}
          variant="filled"
          color="default"
          icon={<IconShare size={18} />}
          shape="circle"
          size="middle"
          aria-label="Share"
          style={{ lineHeight: 1 }}
        />
      ) : (
        <Tooltip title="Share" className={styles.copyLink}>
          <Button
            onClick={() => setIsModalOpen(true)}
            variant="filled"
            color="default"
            icon={<IconShare size={24} />}
            shape="circle"
            size="large"
            aria-label={`Share ${shareType}`}
            style={{ lineHeight: 1 }}
            className={className}
          />
        </Tooltip>
      )}

      <Modal
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        title={
          <span style={{ fontSize: 20, fontWeight: 600, color: "#1a1a1a" }}>
            Share & Invite your friends
          </span>
        }
        centered
        width={480}
        styles={{ body: { padding: "8px 0" } }}
      >
        <div className="mt-4">
          {(displayTitle || eventDetails?.image) && (
            <div className="flex gap-4 p-3 mb-5 border border-gray-200 rounded-xl items-center">
              {eventDetails?.image && (
                <div className="w-[80px] h-[80px] relative rounded-lg overflow-hidden flex-shrink-0 bg-gray-100 border border-gray-100">
                  <Image
                    src={eventDetails.image}
                    alt={displayTitle || "Image"}
                    fill
                    unoptimized
                    style={{ objectFit: "cover" }}
                  />
                </div>
              )}
              <div className="flex flex-col justify-center min-w-0 flex-1">
                {displayTitle && (
                  <Title
                    level={5}
                    style={{ margin: 0, color: "#1a1a1a", fontSize: "16px" }}
                    className="line-clamp-2"
                  >
                    {displayTitle}
                  </Title>
                )}
                {formattedDate ? (
                  <div className="flex items-center gap-2 mt-2">
                    <IconCalendarEvent size={16} color="#6b7280" />
                    <Text className="text-gray-500 text-sm">{formattedDate}</Text>
                  </div>
                ) : venueLocation ? (
                  <div className="flex items-center gap-2 mt-2">
                    <IconMapPin size={16} color="#6b7280" />
                    <Text className="text-gray-500 text-sm truncate">
                      {venueLocation}
                    </Text>
                  </div>
                ) : null}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 mb-6 border border-gray-200 rounded-lg p-1.5 pl-3">
            <div className="flex-1 overflow-hidden">
              <div className="truncate text-gray-500 text-sm">{shareUrl}</div>
            </div>
            <Button
              type="text"
              icon={<IconCopy size={16} />}
              onClick={handleCopy}
              className="bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-md font-medium flex items-center justify-center gap-1.5 px-4 h-9"
            >
              Copy
            </Button>
          </div>

          <div className="mb-8">
            <Title
              level={5}
              style={{
                margin: 0,
                marginBottom: "16px",
                fontSize: "16px",
                color: "#1a1a1a",
              }}
            >
              Social share
            </Title>
            <div className="flex flex-wrap justify-center sm:justify-between gap-x-4 gap-y-5 px-1 sm:px-2">
              <div className="flex flex-col items-center gap-2">
                <EmailShareButton
                  url={shareUrl}
                  subject={displayTitle}
                  body={shareText}
                  separator={"\n\n"}
                >
                  <EmailIcon size={48} round />
                </EmailShareButton>
                <span className="text-xs text-gray-500 font-medium">Email</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <WhatsappShareButton
                  url={shareUrl}
                  title={shareText}
                  separator={"\n\n"}
                >
                  <WhatsappIcon size={48} round />
                </WhatsappShareButton>
                <span className="text-xs text-gray-500 font-medium">
                  WhatsApp
                </span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <FacebookShareButton url={shareUrl} quote={shareText}>
                  <FacebookIcon size={48} round />
                </FacebookShareButton>
                <span className="text-xs text-gray-500 font-medium">
                  Facebook
                </span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <LinkedinShareButton url={shareUrl} title={displayTitle}>
                  <LinkedinIcon size={48} round />
                </LinkedinShareButton>
                <span className="text-xs text-gray-500 font-medium">
                  LinkedIn
                </span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <TwitterShareButton url={shareUrl} title={shareText}>
                  <TwitterIcon size={48} round />
                </TwitterShareButton>
                <span className="text-xs text-gray-500 font-medium">
                  Twitter
                </span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <RedditShareButton url={shareUrl} title={displayTitle}>
                  <RedditIcon size={48} round />
                </RedditShareButton>
                <span className="text-xs text-gray-500 font-medium">
                  Reddit
                </span>
              </div>
              <div
                className="flex flex-col items-center gap-2 cursor-pointer"
                onClick={() => {
                  if (navigator.share) {
                    navigator
                      .share({
                        title: displayTitle,
                        text: shareText,
                        url: shareUrl,
                      })
                      .catch(console.error);
                  } else {
                    staticMessage.info(
                      "Web Share API is not supported in your browser.",
                    );
                  }
                }}
              >
                <div className="w-[48px] h-[48px] rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-gray-200 transition-colors">
                  <svg
                    viewBox="0 0 24 24"
                    width="24"
                    height="24"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="1"></circle>
                    <circle cx="19" cy="12" r="1"></circle>
                    <circle cx="5" cy="12" r="1"></circle>
                  </svg>
                </div>
                <span className="text-xs text-gray-500 font-medium">More</span>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ShareButton;
